import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lightning } from "@phosphor-icons/react";
import GoogleSignInButton from "../components/GoogleSignInButton";
import useSeo from "../lib/useSeo";

export default function Login() {
  useSeo({
    title: "Sign in · Tradelog",
    description: "Sign in to your Tradelog trading journal — import broker CSVs, track daily P&L and take on a prop-firm challenge.",
    canonical: "/login",
    noindex: true,
  });
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 hidden md:flex items-center justify-center bg-secondary/40 border-r border-border p-16">
        <div className="max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-10">
            <Lightning size={22} weight="fill" className="text-primary" />
            <span className="relative inline-block font-display text-xl font-bold leading-none pt-2">
              <span className="absolute right-0 -top-1 text-[7px] font-semibold tracking-[0.15em] uppercase text-primary/80 whitespace-nowrap">by Yash Khajania</span>
              Tradelog
            </span>
          </Link>
          <h1 className="font-display text-4xl font-bold tracking-tight">Welcome back, trader.</h1>
          <p className="mt-4 text-muted-foreground">Log your trades. Own your edge.</p>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <h2 className="font-display text-3xl font-bold">Sign in</h2>
            <p className="text-sm text-muted-foreground mt-1">Enter your credentials</p>
          </div>
          <GoogleSignInButton label="Sign in with Google" testId="login-google-btn" />
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-muted-foreground">
            <span className="flex-1 h-px bg-border" /> or continue with email <span className="flex-1 h-px bg-border" />
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="login-email" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label>Password</Label>
              <Link to="/forgot-password" className="text-[11px] text-primary hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
            </div>
            <Input type="password" required value={password} onChange={e => setPassword(e.target.value)} data-testid="login-password" />
          </div>
          <Button type="submit" disabled={loading} className="w-full rounded-full" data-testid="login-submit">
            {loading ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            No account? <Link to="/signup" className="text-primary underline">Create one</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
